import { puedeDescargar } from "../../../utils/entregarArchivo";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { RootState } from "../../../globals";
import HttpManager from "../../../services/HttpManager";
import { Carpeta, copiarEnlace, Inicio, Nodo } from "../../../services/v2/wappdrive.service";
import Editor from "./editor";
import WappIcono, { TIPOS } from "../../../components/wappIcono";
import Organigrama from "./organigrama";
import Bitacora from "./bitacora";
import Externos from "./externos";
import { Ficha, tipoDe } from "./ficha";
import Buscador, { Resaltar } from "./buscador";
import { DlgCompartir, DlgEspacio, DlgMover, DlgNombre, DlgVersiones, DlgVista, esFoto, errorTexto, fecha, peso } from "./dialogos";
import "./wappdrive.css";

type Vista = { tipo: "espacio"; space: number; parent: number | null } | { tipo: "compartidos" | "recientes" | "papelera" | "organigrama" | "equipo" | "areas" | "bitacora" | "externos" } | { tipo: "buscar"; q: string; filtro?: string; desde?: string };
interface Subida { id: string; nombre: string; avance: number; error?: string; listo?: boolean; }

/**
 * WappDrive (Esteban, 2026-10-09): el reemplazo de Zoho WorkDrive. Mis archivos, Mi equipo (la jefa y las de arriba ven y
 * editan lo de su gente), Carpetas del área, Compartidos conmigo, Recientes y Papelera. WappDocs / WappCell se abren en
 * el editor y varias personas editan a la vez.
 */
const WappdriveController: React.FC = () => {
	const http = HttpManager.getInstance().wappdriveService;
	const sessionCampus: any = useSelector((state: RootState) => state.rootReducer.sessionState.currentCampus);
	const legacyCampusKey: any = useSelector((state: RootState) => state.currentUserCampus.value);
	const legacyCampusRef: any = useSelector((state: RootState) => state.campusRef.value);
	const campusId: number = (legacyCampusKey && legacyCampusRef ? legacyCampusRef[legacyCampusKey]?.id : null) ?? sessionCampus?.id ?? 0;

	const [ini, setIni] = useState<Inicio | null>(null);
	const [vista, setVista] = useState<Vista | null>(null);
	const [datos, setDatos] = useState<Carpeta | null>(null);
	const [items, setItems] = useState<Nodo[]>([]);
	const [sel, setSel] = useState<number[]>([]);
	const [orden, setOrden] = useState<string>("nombre");
	const [cuadricula, setCuadricula] = useState<boolean>(() => { try { return localStorage.getItem("wd.cuadricula") === "1"; } catch (e) { return false; } });
	const [q, setQ] = useState<string>("");
	const [cargando, setCargando] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [aviso, setAviso] = useState<string | null>(null);
	const [nuevo, setNuevo] = useState<boolean>(false);
	const [menu, setMenu] = useState<{ n: Nodo; x: number; y: number } | null>(null);
	const [dlg, setDlg] = useState<any>(null);
	const [editor, setEditor] = useState<number | null>(null);
	const [vistaPrevia, setVistaPrevia] = useState<{ n: Nodo; url: string } | null>(null);
	const [subidas, setSubidas] = useState<Subida[]>([]);
	const [arrastre, setArrastre] = useState<boolean>(false);
	const [menuSubir, setMenuSubir] = useState<boolean>(false);
	const archivoRef = useRef<HTMLInputElement>(null);
	const carpetaRef = useRef<HTMLInputElement>(null);

	const cargarInicio = () => http.inicio(campusId).then((r) => { setIni(r.data); return r.data; });
	useEffect(() => {
		if (!campusId) return;
		cargarInicio().then((d) => {
			const p = new URLSearchParams(window.location.search);
			const abrir = Number(p.get("abrir") || 0);
			// ?abrir= solo lleva al editor si el archivo es de oficina; una foto o un video se ven en su visor (Esteban, 2026-10-13:
			// "lo está abriendo en WappDocs").
			if (abrir) {
				window.history.replaceState(null, "", window.location.pathname);
				http.info(campusId, abrir).then(({ data: n }) => {
					if (n.editor) setEditor(n.id);
					else if (!n.is_folder) http.descargar(campusId, n.id).then((r) => setVistaPrevia({ n: n as any, url: r.data.ver }));
				}).catch((e) => setError(errorTexto(e)));
			}
			const sp = Number(p.get("space") || 0), pa = Number(p.get("parent") || 0);
			if (sp) window.history.replaceState(null, "", window.location.pathname);
			setVista({ tipo: "espacio", space: sp || d.personal, parent: pa || null });
			// Enlaces de carpeta (?carpeta=) y de otros archivos (?ver=), como Google Drive. Si no hay acceso, avisa.
			const carpeta = Number(p.get("carpeta") || 0), ver = Number(p.get("ver") || 0), doc = Number(p.get("d") || 0);
			const porEnlace = carpeta || ver || doc;
			if (porEnlace) http.info(campusId, porEnlace).then(({ data: n }) => {
				if (n.is_folder) setVista({ tipo: "espacio", space: n.space_id, parent: n.id });
				else if (n.editor) setEditor(n.id);
				else { setVista({ tipo: "espacio", space: n.space_id, parent: n.parent_id }); http.descargar(campusId, n.id).then((r) => setVistaPrevia({ n: n as any, url: r.data.ver })); }
			}).catch((e) => setError(errorTexto(e)));
		}).catch((e) => setError(errorTexto(e)));
	}, [campusId]);

	// Carpetas enormes (p. ej. 62,914 archivos en "Attachments CRM Anterior"): 200 por página y la siguiente al acercarse al
	// final de la lista, como Google Drive (Esteban, 2026-10-09).
	const [cargandoMas, setCargandoMas] = useState<boolean>(false);
	const finLista = useRef<HTMLDivElement>(null);
	const cargarMas = async () => {
		if (!vista || vista.tipo !== "espacio" || !datos?.hayMas || cargandoMas) return;
		setCargandoMas(true);
		try {
			const r = await http.carpeta(campusId, vista.space, vista.parent, orden, (datos.pagina || 1) + 1);
			setItems((a) => [...a, ...r.data.items]);
			setDatos((d) => (d ? { ...d, pagina: r.data.pagina, hayMas: r.data.hayMas, total: r.data.total } : d));
		} catch (e) { setError(errorTexto(e)); } finally { setCargandoMas(false); }
	};
	useEffect(() => {
		const el = finLista.current;
		if (!el || !datos?.hayMas) return;
		const io = new IntersectionObserver((e) => { if (e[0]?.isIntersecting) cargarMas(); }, { rootMargin: "600px" });
		io.observe(el);
		return () => io.disconnect();
	}, [datos?.hayMas, datos?.pagina, cargandoMas, items.length]);

	const cargar = async () => {
		if (!vista) return;
		setCargando(true); setError(null); setSel([]);
		try {
			if (vista.tipo === "espacio") { const r = await http.carpeta(campusId, vista.space, vista.parent, orden); setDatos(r.data); setItems(r.data.items); }
			else if (vista.tipo === "buscar") { setDatos(null); setItems((await http.buscar(campusId, vista.q, { tipo: vista.filtro, desde: vista.desde })).data.items); }
			else if (vista.tipo === "organigrama" || vista.tipo === "equipo" || vista.tipo === "areas" || vista.tipo === "bitacora" || vista.tipo === "externos") { setDatos(null); setItems([]); }
			else { setDatos(null); setItems((await (vista.tipo === "compartidos" ? http.compartidos(campusId) : vista.tipo === "recientes" ? http.recientes(campusId) : http.papelera(campusId))).data.items); }
		} catch (e) { setError(errorTexto(e)); } finally { setCargando(false); }
	};
	useEffect(() => { cargar(); }, [vista, orden]);
	// La dirección del navegador sigue a la carpeta abierta (/wappdrive?carpeta=123), para copiarla y mandarla como en Google Drive.
	useEffect(() => {
		if (editor || !vista) return;
		const url = vista.tipo === "espacio" ? (vista.parent ? "/wappdrive?carpeta=" + vista.parent : vista.space !== ini?.personal ? "/wappdrive?space=" + vista.space : "/wappdrive") : "/wappdrive";
		if (window.location.pathname === "/wappdrive" && window.location.pathname + window.location.search !== url) window.history.replaceState(null, "", url);
	}, [vista, editor]);
	useEffect(() => { try { localStorage.setItem("wd.cuadricula", cuadricula ? "1" : "0"); } catch (e) { /* sin almacenamiento */ } }, [cuadricula]);
	useEffect(() => { const c = () => { setMenu(null); setMenuSubir(false); }; window.addEventListener("click", c); return () => window.removeEventListener("click", c); }, []);

	const enCarpeta = vista?.tipo === "espacio";
	const puedeEditar = enCarpeta && datos && datos.permiso !== "ver";
	const destino = (): [number | null, number | null] => (vista?.tipo === "espacio" ? [vista.space, vista.parent] : [ini?.personal || null, null]);
	const irEspacio = (space: number, parent: number | null = null) => { setVista({ tipo: "espacio", space, parent }); setQ(""); };
	const accion = async (fn: () => Promise<any>, ok?: string) => { setError(null); try { await fn(); if (ok) setAviso(ok); await cargar(); } catch (e) { setError(errorTexto(e)); } };

	const abrir = async (n: Nodo) => {
		if (vista?.tipo === "papelera") return;
		if (n.is_folder) { irEspacio(n.space_id, n.id); return; }
		if (n.pendiente) { setError("Este archivo todavía está pasando de WorkDrive. Intenta en un rato."); return; }
		if (n.editor) { setEditor(n.id); return; }
		try { const r = await http.descargar(campusId, n.id); setVistaPrevia({ n, url: r.data.ver }); } catch (e) { setError(errorTexto(e)); }
	};
	// Fotos de la carpeta abierta: el visor pasa entre ellas con ← → (como la galería del teléfono).
	const fotos = vistaPrevia && esFoto(vistaPrevia.n) ? items.filter(esFoto) : [];
	const iFoto = vistaPrevia ? fotos.findIndex((x) => x.id === vistaPrevia.n.id) : -1;
	const fotoVecina = (paso: number): (() => void) | undefined => {
		if (iFoto < 0 || fotos.length < 2) return undefined;
		const sig = fotos[(iFoto + paso + fotos.length) % fotos.length];
		return () => { http.descargar(campusId, sig.id).then((r) => setVistaPrevia({ n: sig, url: r.data.ver })).catch((e) => setError(errorTexto(e))); };
	};
	const posicionFoto = () => (iFoto >= 0 && fotos.length > 1 ? (iFoto + 1) + " de " + fotos.length : undefined);
	// Descargar a la computadora solo quien puede sacar archivos de WAPP; los demás ya los tienen en WappDrive.
	const [puedeBajar, setPuedeBajar] = useState<boolean>(false);
	useEffect(() => { puedeDescargar().then(setPuedeBajar); }, []);
	const descargar = async (n: Nodo) => { try { const u = (await http.descargar(campusId, n.id, true)).data.url; if (u) window.location.href = u; } catch (e) { setError(errorTexto(e)); } };

	const subir = async (files: File[], rutas = false) => {
		const [space, parent0] = destino();
		if (!space) return;
		const carpetas: Record<string, number | null> = { "": parent0 };
		const asegurar = async (ruta: string): Promise<number | null> => {
			if (ruta in carpetas) return carpetas[ruta];
			const i = ruta.lastIndexOf("/");
			const padre = await asegurar(i > 0 ? ruta.slice(0, i) : "");
			const r = await http.crearCarpeta(campusId, padre ? null : space, padre, ruta.slice(i + 1));
			carpetas[ruta] = r.data.id;
			return r.data.id;
		};
		for (const f of files) {
			const id = Math.random().toString(36).slice(2);
			setSubidas((s) => [...s, { id, nombre: f.name, avance: 0 }]);
			try {
				const rel = rutas ? ((f as any).webkitRelativePath || "") : "";
				const dir = rel.includes("/") ? rel.slice(0, rel.lastIndexOf("/")) : "";
				const parent = dir ? await asegurar(dir) : parent0;
				await http.subir(campusId, parent ? null : space, parent, f, (p) => setSubidas((s) => s.map((x) => (x.id === id ? { ...x, avance: p } : x))));
				setSubidas((s) => s.map((x) => (x.id === id ? { ...x, avance: 1, listo: true } : x)));
			} catch (e) { setSubidas((s) => s.map((x) => (x.id === id ? { ...x, error: errorTexto(e) } : x))); }
		}
		cargar();
		setTimeout(() => setSubidas((s) => s.filter((x) => !x.listo)), 4000);
	};

	const soltar = async (e: React.DragEvent) => {
		e.preventDefault(); setArrastre(false);
		if (!puedeEditar) { setError("No puedes subir archivos aquí."); return; }
		// Carpetas arrastradas: se recorren y se suben con su estructura.
		const entradas = Array.from(e.dataTransfer.items || []).map((it: any) => it.webkitGetAsEntry?.()).filter(Boolean);
		if (entradas.some((x: any) => x.isDirectory)) {
			const out: File[] = [];
			const leer = async (ent: any, ruta: string): Promise<void> => {
				if (ent.isFile) {
					const f: File = await new Promise((ok, mal) => ent.file(ok, mal));
					Object.defineProperty(f, "webkitRelativePath", { value: ruta + f.name });
					out.push(f);
				} else if (ent.isDirectory) {
					const r = ent.createReader();
					let lote: any[] = [];
					do { lote = await new Promise((ok, mal) => r.readEntries(ok, mal)); for (const x of lote) await leer(x, ruta + ent.name + "/"); } while (lote.length);
				}
			};
			for (const x of entradas) await leer(x, "");
			if (out.length) subir(out, true);
			return;
		}
		const fs = Array.from(e.dataTransfer.files || []);
		if (fs.length) subir(fs);
	};

	const seleccionados = useMemo(() => items.filter((n) => sel.includes(n.id)), [items, sel]);
	const borrarSel = async (ns: Nodo[]) => {
		if (!window.confirm(ns.length > 1 ? "¿Mandar " + ns.length + " elementos a la papelera?" : "¿Mandar “" + ns[0].name + "” a la papelera?")) return;
		accion(async () => { for (const n of ns) await http.borrar(campusId, n.id); }, "Enviado a la papelera.");
	};

	if (!campusId) return null;
	if (editor) return <Editor campusId={campusId} id={editor} onCerrar={() => { setEditor(null); cargar(); }} />;

	const titulo = vista?.tipo === "equipo" ? "Equipo" : vista?.tipo === "areas" ? "Carpetas del área" : vista?.tipo === "espacio" ? datos?.espacio.name : vista?.tipo === "compartidos" ? "Compartidos conmigo" : vista?.tipo === "recientes" ? "Recientes"
		: vista?.tipo === "papelera" ? "Papelera" : vista?.tipo === "organigrama" ? "Organigrama" : vista?.tipo === "bitacora" ? "Bitácora de compartidos" : vista?.tipo === "externos" ? "Sacar Archivos de WAPP" : vista?.tipo === "buscar" ? "Resultados de búsqueda" : "";
	const areas = (ini?.espacios || []).filter((e) => e.kind === "equipo");
	const esPersonalAjena = vista?.tipo === "espacio" && vista.space !== ini?.personal && datos?.espacio.kind === "personal";

	return (
		<div className="wd">
			<aside className="wd-riel">
				<div className="wd-nuevo-caja">
					<button className="wd-btn pri grande" disabled={!puedeEditar && enCarpeta} onClick={(e) => { e.stopPropagation(); setNuevo(!nuevo); }}>+ Nuevo</button>
					{nuevo ? (
						<div className="wd-menu" onClick={(e) => e.stopPropagation()}>
							<button onClick={() => { setNuevo(false); setDlg({ t: "carpeta" }); }}><Ficha n={{ is_folder: true } as any} /> Carpeta</button>
							<button onClick={() => { setNuevo(false); setDlg({ t: "doc", tipo: "docx" }); }}><Ficha n={{ editor: "word" } as any} /> WappDocs</button>
							<button onClick={() => { setNuevo(false); setDlg({ t: "doc", tipo: "xlsx" }); }}><Ficha n={{ editor: "cell" } as any} /> WappCell</button>
							<button onClick={() => { setNuevo(false); setDlg({ t: "doc", tipo: "pptx" }); }}><Ficha n={{ editor: "slide" } as any} /> WappDeck</button>
							<hr />
							<button onClick={() => { setNuevo(false); archivoRef.current?.click(); }}><span className="wd-mi"><WappIcono n="upload" /></span> Subir archivos</button>
							<button onClick={() => { setNuevo(false); carpetaRef.current?.click(); }}><span className="wd-mi"><WappIcono n="folders" /></span> Subir carpeta</button>
						</div>
					) : null}
					<input ref={archivoRef} type="file" multiple hidden onChange={(e) => { const f = Array.from(e.target.files || []); e.target.value = ""; if (f.length) subir(f); }} />
					<input ref={carpetaRef} type="file" multiple hidden {...({ webkitdirectory: "", directory: "" } as any)} onChange={(e) => { const f = Array.from(e.target.files || []); e.target.value = ""; if (f.length) subir(f, true); }} />
				</div>
				<nav>
					<button className={vista?.tipo === "espacio" && vista.space === ini?.personal ? "on" : ""} onClick={() => ini && irEspacio(ini.personal)}><WappIcono n="folder" /> Mis archivos</button>
					<button className={vista?.tipo === "compartidos" ? "on" : ""} onClick={() => setVista({ tipo: "compartidos" })}><WappIcono n="share" /> Compartidos conmigo{ini?.compartidos ? <b>{ini.compartidos}</b> : null}</button>
					<button className={vista?.tipo === "recientes" ? "on" : ""} onClick={() => setVista({ tipo: "recientes" })}><WappIcono n="clock" /> Recientes</button>
					{ini?.equipo?.length ? (
						<>
							<div className="wd-riel-tit">Equipo</div>
							<div className="wd-riel-lista">
								{ini.equipo.map((p: any) => (
									<button key={p.id} title={p.zoho_rol || ""} onClick={() => accion(async () => { const r = await http.carpetaDe(campusId, p.id); await cargarInicio(); irEspacio(r.data.space); })}><WappIcono n="user" /> {p.name}</button>
								))}
							</div>
						</>
					) : null}
					{/* Roles de base (asesoras, profesores, agentes): solo lo suyo y lo compartido, sin carpetas del área (Esteban, 2026-10-09). */}
					{ini?.soloLoSuyo && !areas.length ? null : (
						<>
							<div className="wd-riel-tit">Carpetas del área {ini?.super ? <button className="wd-mas" title="Nueva carpeta del área" onClick={() => setDlg({ t: "espacio", id: null })}>+</button> : null}</div>
							<div className="wd-riel-lista">
								{/* Compartir la carpeta del área desde la barra (Esteban, 2026-10-13: "no se pueden compartir"): ícono al pasar el mouse o clic derecho. */}
								{areas.map((e) => (
									<div key={e.id} className={"wd-riel-fila" + (vista?.tipo === "espacio" && vista.space === e.id ? " on" : "")}>
										<button className={vista?.tipo === "espacio" && vista.space === e.id ? "on" : ""} onClick={() => irEspacio(e.id)} onContextMenu={(ev) => { ev.preventDefault(); setDlg({ t: "espacio", id: e.id }); }} title={e.name}><WappIcono n="folderShared" /> {e.name}</button>
										<button className="wd-riel-compartir" title="Compartir" aria-label={"Compartir " + e.name} onClick={() => setDlg({ t: "espacio", id: e.id })}><WappIcono n="share" /></button>
									</div>
								))}
								{!areas.length ? <span className="wd-tenue">Ninguna todavía</span> : null}
							</div>
						</>
					)}
					<button className={vista?.tipo === "papelera" ? "on" : ""} onClick={() => setVista({ tipo: "papelera" })}><WappIcono n="trash" /> Papelera</button>
					{ini?.organigrama ? <button className={vista?.tipo === "organigrama" ? "on" : ""} onClick={() => setVista({ tipo: "organigrama" })}><WappIcono n="sitemap" /> Organigrama</button> : null}
					{ini?.super ? <button className={vista?.tipo === "bitacora" ? "on" : ""} onClick={() => setVista({ tipo: "bitacora" })}><WappIcono n="history" /> Bitácora</button> : null}
					{ini?.super ? <button className={vista?.tipo === "externos" ? "on" : ""} onClick={() => setVista({ tipo: "externos" })}><WappIcono n="world" /> Sacar Archivos de WAPP</button> : null}
				</nav>
			</aside>

			<main className={"wd-main" + (arrastre ? " arrastre" : "")} onDragOver={(e) => { if (puedeEditar) { e.preventDefault(); setArrastre(true); } }} onDragLeave={() => setArrastre(false)} onDrop={soltar}>
				<div className="wd-pestanas" role="tablist">
					<button className={vista?.tipo === "espacio" && vista.space === ini?.personal ? "on" : ""} onClick={() => ini && irEspacio(ini.personal)}>Míos</button>
					{ini?.equipo?.length ? <button className={vista?.tipo === "equipo" || esPersonalAjena ? "on" : ""} onClick={() => setVista({ tipo: "equipo" })}>Equipo</button> : null}
					<button className={vista?.tipo === "areas" || (vista?.tipo === "espacio" && datos?.espacio.kind === "equipo") ? "on" : ""} onClick={() => setVista({ tipo: "areas" })}>Área</button>
					<button className={vista?.tipo === "compartidos" ? "on" : ""} onClick={() => setVista({ tipo: "compartidos" })}>Compartidos</button>
				</div>
				{ini?.super || ini?.organigrama ? (
					// En celular el riel no se ve: Organigrama, Bitácora y Compartir con externos quedan aquí (solo quien los puede usar).
					<div className="wd-admin-movil">
						{ini?.organigrama ? <button className={vista?.tipo === "organigrama" ? "on" : ""} onClick={() => setVista({ tipo: "organigrama" })}><WappIcono n="sitemap" /> Organigrama</button> : null}
						{ini?.super ? <button className={vista?.tipo === "bitacora" ? "on" : ""} onClick={() => setVista({ tipo: "bitacora" })}><WappIcono n="history" /> Bitácora</button> : null}
						{ini?.super ? <button className={vista?.tipo === "externos" ? "on" : ""} onClick={() => setVista({ tipo: "externos" })}><WappIcono n="world" /> Sacar Archivos</button> : null}
					</div>
				) : null}
				<div className="wd-barra">
					<Buscador campusId={campusId} valor={q} onValor={setQ} onBuscar={(t) => setVista({ tipo: "buscar", q: t })} onAbrir={(n) => (n.is_folder ? irEspacio(n.space_id, n.id) : abrir(n))} />
					<span style={{ flex: 1 }} />
					{vista?.tipo === "espacio" ? (
						<select value={orden} onChange={(e) => setOrden(e.target.value)} aria-label="Ordenar"><option value="nombre">Nombre</option><option value="fecha">Modificado</option><option value="tamano">Tamaño</option></select>
					) : null}
					<button className="wd-btn" onClick={() => setCuadricula(!cuadricula)} title={cuadricula ? "Ver como lista" : "Ver como cuadrícula"}>{cuadricula ? <WappIcono n="list" /> : <WappIcono n="grid" />}</button>
				</div>

				<div className="wd-cab">
					{vista?.tipo === "espacio" && datos ? (
						<div className="wd-migas">
							<button onClick={() => irEspacio(datos.espacio.id)}>{datos.espacio.name}</button>
							{datos.migas.map((m) => <React.Fragment key={m.id}><span>›</span><button onClick={() => irEspacio(datos.espacio.id, m.id)}>{m.name}</button></React.Fragment>)}
						</div>
					) : <h1>{titulo}</h1>}
					{/* Compartir lo que se está viendo: en la raíz de un área, quién entra al área; dentro de una carpeta, esa carpeta. */}
					{vista?.tipo === "espacio" && datos && (datos.carpeta || datos.espacio.kind === "equipo") ? (
						<button className="wd-btn chico" onClick={() => setDlg(datos.carpeta
							? { t: "compartir", n: { id: datos.carpeta, name: datos.migas[datos.migas.length - 1]?.name || "", is_folder: true, space_id: datos.espacio.id, permiso: datos.permiso } }
							: { t: "espacio", id: datos.espacio.id })}><WappIcono n="share" /> Compartir</button>
					) : null}
					{vista?.tipo === "espacio" && datos && (datos.total || 0) > 0 ? <span className="wd-tenue wd-cuenta">{datos.total.toLocaleString("es-MX")} {datos.total === 1 ? "elemento" : "elementos"}</span> : null}
					{esPersonalAjena ? <span className="wd-chip">Carpeta de tu equipo</span> : null}
					{vista?.tipo === "buscar" ? (
						<div className="wd-filtros-busca">
							<span className="wd-tenue">{items.length >= 200 ? "Más de 200" : items.length} {items.length === 1 ? "resultado" : "resultados"} para “{vista.q}”</span>
							<label className={"wd-filtro" + (vista.filtro ? " on" : "")}><WappIcono n="file" />
								<select value={vista.filtro || ""} onChange={(e) => setVista({ ...vista, filtro: e.target.value || undefined })} aria-label="Tipo">
									<option value="">Tipo</option><option value="carpeta">Carpetas</option><option value="word">WappDocs</option><option value="cell">WappCell</option><option value="slide">WappDeck</option><option value="pdf">PDF</option><option value="foto">Imágenes</option><option value="video">Videos</option>
								</select>
							</label>
							<label className={"wd-filtro" + (vista.desde ? " on" : "")}><WappIcono n="clock" />
								<select value={vista.desde || ""} onChange={(e) => setVista({ ...vista, desde: e.target.value || undefined })} aria-label="Modificado">
									<option value="">Modificado</option><option value="hoy">Hoy</option><option value="7">Últimos 7 días</option><option value="30">Últimos 30 días</option><option value="365">Último año</option>
								</select>
							</label>
							{vista.filtro || vista.desde ? <button className="wd-btn chico" onClick={() => setVista({ tipo: "buscar", q: vista.q })}>Quitar filtros</button> : null}
						</div>
					) : null}
					{puedeEditar ? (
						<div className="wd-subir-caja">
							<button className="wd-subir" onClick={(e) => { e.stopPropagation(); archivoRef.current?.click(); }}><WappIcono n="upload" /> Subir</button>
							<button className="wd-subir-mas" aria-label="Más opciones para subir" onClick={(e) => { e.stopPropagation(); setMenuSubir(!menuSubir); }}><WappIcono n="back" className="wd-giro" /></button>
							{menuSubir ? (
								<div className="wd-menu wd-menu-subir" onClick={(e) => e.stopPropagation()}>
									<button onClick={() => { setMenuSubir(false); archivoRef.current?.click(); }}><span className="wd-mi"><WappIcono n="file" /></span> Archivos</button>
									<button onClick={() => { setMenuSubir(false); carpetaRef.current?.click(); }}><span className="wd-mi"><WappIcono n="folders" /></span> Carpeta completa</button>
								</div>
							) : null}
						</div>
					) : null}
				</div>

				{sel.length ? (
					<div className="wd-seleccion">
						<b>{sel.length} seleccionado{sel.length > 1 ? "s" : ""}</b>
						{puedeEditar ? <button className="wd-btn chico" onClick={() => setDlg({ t: "mover", nodos: seleccionados })}>Mover</button> : null}
						{puedeEditar ? <button className="wd-btn chico" onClick={() => borrarSel(seleccionados)}>Eliminar</button> : null}
						<button className="wd-btn chico" onClick={() => setSel([])}>Quitar selección</button>
					</div>
				) : null}

				{error ? <div className="wd-aviso" onClick={() => setError(null)}>{error}</div> : null}
				{aviso ? <div className="wd-aviso ok" onClick={() => setAviso(null)}>{aviso}</div> : null}

				{vista?.tipo === "equipo" || vista?.tipo === "areas" ? (
					<div className="wd-tabla">
						{(vista.tipo === "equipo" ? (ini?.equipo || []).map((p: any) => ({ id: p.id, name: p.name, sub: p.zoho_rol || "", persona: true }))
							: areas.map((e) => ({ id: e.id, name: e.name, sub: e.descripcion || "", persona: false }))).map((x) => (
							<button key={x.id} className="wd-fila wd-fila-lista" onClick={() => x.persona ? accion(async () => { const r = await http.carpetaDe(campusId, x.id); await cargarInicio(); irEspacio(r.data.space); }) : irEspacio(x.id)}>
								<span className="wd-tipo" style={{ background: x.persona ? "#EFE9E4" : "#FDE7E0", color: x.persona ? "#6B5B50" : "#C63A1F" }}><WappIcono n={x.persona ? "user" : "folderShared"} /></span>
								<span className="wd-texto"><b>{x.name}</b>{x.sub ? <small>{x.sub}</small> : null}</span>
							</button>
						))}
					</div>
				) : vista?.tipo === "organigrama" ? <Organigrama campusId={campusId} />
				: vista?.tipo === "externos" ? <Externos campusId={campusId} onBitacora={() => setVista({ tipo: "bitacora" })} />
				: vista?.tipo === "bitacora" ? <Bitacora campusId={campusId} onAbrir={(id, sp, pa, carpeta) => (carpeta ? irEspacio(sp, id) : irEspacio(sp, pa))} /> : cargando && !items.length ? <div className="wd-vacio">Cargando…</div> : !items.length ? (
					vista?.tipo === "espacio" && puedeEditar ? (
						<button className="wd-soltar-aqui" onClick={() => archivoRef.current?.click()}>
							<span className="wd-soltar-ico"><WappIcono n="upload" /></span>
							<b>Arrastra archivos o carpetas aquí</b>
							<span>o toca para elegirlos · hasta 5 GB por archivo</span>
						</button>
					) : <div className="wd-vacio">{vista?.tipo === "espacio" ? "Esta carpeta está vacía." : vista?.tipo === "papelera" ? "La papelera está vacía." : "Nada por aquí."}</div>
				) : cuadricula ? (
					<div className="wd-cuadricula">
						{items.map((n) => (
							<div key={n.id} className={"wd-tarjeta" + (sel.includes(n.id) ? " sel" : "")} onDoubleClick={() => abrir(n)} onClick={(e) => { if (e.metaKey || e.ctrlKey) setSel(sel.includes(n.id) ? sel.filter((x) => x !== n.id) : [...sel, n.id]); else abrir(n); }}
								onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); setMenu({ n, x: e.clientX, y: e.clientY }); }}>
								<Ficha n={n} grande />
								<b title={n.name}>{n.name}</b>
								<small>{n.is_folder ? "Carpeta" : peso(n.size)} · {fecha(n.updated_at)}</small>
							</div>
						))}
					</div>
				) : (
					<div className="wd-tabla" role="table">
						<div className="wd-fila cab" role="row">
							<span><input type="checkbox" checked={sel.length === items.length} onChange={(e) => setSel(e.target.checked ? items.map((n) => n.id) : [])} aria-label="Seleccionar todo" /></span>
							<span>Nombre</span><span className="wd-col-m wd-col-dueno">{vista?.tipo === "papelera" ? "Dónde estaba" : vista?.tipo === "buscar" ? "Ubicación" : "Dueño"}</span><span className="wd-col-m">Modificado</span><span className="wd-col-s">Tamaño</span><span />
						</div>
						{items.map((n) => (
							<div key={n.id} role="row" className={"wd-fila" + (sel.includes(n.id) ? " sel" : "") + (n.pendiente ? " pendiente" : "")} onDoubleClick={() => abrir(n)}
								onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); setMenu({ n, x: e.clientX, y: e.clientY }); }}>
								<span><input type="checkbox" checked={sel.includes(n.id)} onChange={() => setSel(sel.includes(n.id) ? sel.filter((x) => x !== n.id) : [...sel, n.id])} aria-label={"Seleccionar " + n.name} /></span>
								<button className="wd-nombre" onClick={() => abrir(n)}><Ficha n={n} /><span className="wd-texto">{vista?.tipo === "buscar" ? <Resaltar texto={n.name} q={vista.q} /> : n.name}</span>{n.compartido ? <span className="wd-chip">compartido</span> : null}{n.pendiente ? <span className="wd-chip oro">pasando de WorkDrive</span> : null}</button>
								<span className="wd-col-m wd-col-dueno wd-tenue">{vista?.tipo === "papelera" || vista?.tipo === "buscar" ? n.ruta : n.owner || n.zoho_creador || "—"}</span>
								<span className="wd-col-m wd-tenue">{fecha(vista?.tipo === "papelera" ? n.deleted_at : n.updated_at)}{n.updated_by && vista?.tipo !== "papelera" ? " · " + n.updated_by.split(" ")[0] : ""}</span>
								<span className="wd-col-s wd-tenue">{n.is_folder ? "—" : peso(n.size)}</span>
								<span><button className="wd-mas" onClick={(e) => { e.stopPropagation(); const r = (e.target as HTMLElement).getBoundingClientRect(); setMenu({ n, x: r.right, y: r.bottom }); }} aria-label="Acciones"><WappIcono n="dots" /></button></span>
							</div>
						))}
					</div>
				)}
				{vista?.tipo === "espacio" && datos?.hayMas ? (
					<div ref={finLista} className="wd-mas-abajo">
						{cargandoMas ? "Cargando más…" : <button className="wd-btn" onClick={cargarMas}>Ver más</button>}
						<span className="wd-tenue">{items.length.toLocaleString("es-MX")} de {(datos.total || 0).toLocaleString("es-MX")}</span>
					</div>
				) : null}
				{arrastre ? <div className="wd-soltar">Suelta para subir aquí</div> : null}
			</main>

			{menu ? (
				<div className="wd-menu flotante" style={{ left: Math.min(menu.x, window.innerWidth - 220), top: Math.min(menu.y, window.innerHeight - 320) }} onClick={(e) => e.stopPropagation()}>
					{vista?.tipo === "papelera" ? (
						<>
							<button onClick={() => { setMenu(null); accion(() => http.restaurar(campusId, menu.n.id), "Restaurado."); }}><WappIcono n="restore" /> Restaurar</button>
							<button onClick={() => { setMenu(null); if (window.confirm("¿Borrar para siempre “" + menu.n.name + "”? No se puede deshacer.")) accion(() => http.borrarDefinitivo(campusId, menu.n.id), "Borrado para siempre."); }}><WappIcono n="trash" /> Borrar para siempre</button>
						</>
					) : (
						<>
							<button onClick={() => { setMenu(null); abrir(menu.n); }}>{menu.n.is_folder ? <><WappIcono n="open" /> Abrir</> : menu.n.editor ? <><WappIcono n="edit" /> Abrir en {TIPOS[menu.n.editor]?.nombre}</> : <><WappIcono n="eye" /> Vista previa</>}</button>
							{!menu.n.is_folder && puedeBajar ? <button onClick={() => { setMenu(null); descargar(menu.n); }}><WappIcono n="download" /> Descargar</button> : null}
							<button onClick={() => { setMenu(null); setDlg({ t: "compartir", n: menu.n }); }}><WappIcono n="share" /> Compartir</button>
							<button onClick={() => { const n = menu.n; setMenu(null); copiarEnlace(n).then(() => setAviso("Enlace copiado.")); }}><WappIcono n="link" /> Copiar enlace</button>
							{!menu.n.is_folder ? <button onClick={() => { const n = menu.n; setMenu(null); accion(() => http.copiar(campusId, n.id), "Copia creada."); }}><WappIcono n="copy" /> Hacer una copia</button> : null}
							{menu.n.permiso !== "ver" ? (
								<>
									<button onClick={() => { setMenu(null); setDlg({ t: "renombrar", n: menu.n }); }}><WappIcono n="edit" /> Renombrar</button>
									<button onClick={() => { setMenu(null); setDlg({ t: "mover", nodos: [menu.n] }); }}><WappIcono n="move" /> Mover</button>
								</>
							) : null}
							{!menu.n.is_folder ? <button onClick={() => { setMenu(null); setDlg({ t: "versiones", n: menu.n }); }}><WappIcono n="history" /> Versiones</button> : null}
							{vista?.tipo !== "espacio" ? <button onClick={() => { setMenu(null); irEspacio(menu.n.space_id, menu.n.parent_id); }}><WappIcono n="pin" /> Ir a su carpeta</button> : null}
							{menu.n.permiso !== "ver" ? <button className="peligro" onClick={() => { setMenu(null); borrarSel([menu.n]); }}><WappIcono n="trash" /> Eliminar</button> : null}
						</>
					)}
				</div>
			) : null}

			{nuevo ? (
				<div className="wd-menu wd-menu-movil" onClick={(e) => e.stopPropagation()}>
					<button onClick={() => { setNuevo(false); setDlg({ t: "carpeta" }); }}><Ficha n={{ is_folder: true } as any} /> Carpeta</button>
					<button onClick={() => { setNuevo(false); setDlg({ t: "doc", tipo: "docx" }); }}><Ficha n={{ editor: "word" } as any} /> WappDocs</button>
					<button onClick={() => { setNuevo(false); setDlg({ t: "doc", tipo: "xlsx" }); }}><Ficha n={{ editor: "cell" } as any} /> WappCell</button>
					<button onClick={() => { setNuevo(false); setDlg({ t: "doc", tipo: "pptx" }); }}><Ficha n={{ editor: "slide" } as any} /> WappDeck</button>
					<button onClick={() => { setNuevo(false); archivoRef.current?.click(); }}><span className="wd-mi"><WappIcono n="upload" /></span> Subir archivos</button>
				</div>
			) : null}
			{puedeEditar ? <button className="wd-fab" aria-label="Nuevo" onClick={(e) => { e.stopPropagation(); setNuevo(!nuevo); }}><WappIcono n="plus" /></button> : null}
			{subidas.length ? (
				<div className="wd-subidas">
					<b>Subiendo {subidas.filter((s) => !s.listo && !s.error).length ? subidas.filter((s) => !s.listo && !s.error).length + " archivo(s)" : ""}</b>
					{subidas.map((s) => (
						<div key={s.id} className={"wd-subida" + (s.error ? " error" : "")}>
							<span title={s.nombre}>{s.nombre}</span>
							{s.error ? <small>{s.error}</small> : <i style={{ width: Math.round(s.avance * 100) + "%" }} />}
						</div>
					))}
					{!subidas.some((s) => !s.listo && !s.error) ? <button className="wd-btn chico" onClick={() => setSubidas([])}>Cerrar</button> : null}
				</div>
			) : null}

			{dlg?.t === "carpeta" ? <DlgNombre titulo="Nueva carpeta" inicial="Carpeta nueva" boton="Crear" onClose={() => setDlg(null)} onOk={async (v) => { const [s, p] = destino(); await http.crearCarpeta(campusId, p ? null : s, p, v); setDlg(null); cargar(); }} /> : null}
			{dlg?.t === "doc" ? (
				<DlgNombre titulo={dlg.tipo === "docx" ? "Nuevo WappDocs" : dlg.tipo === "xlsx" ? "Nuevo WappCell" : "Nuevo WappDeck"} inicial={dlg.tipo === "docx" ? "Documento sin título" : dlg.tipo === "xlsx" ? "Hoja sin título" : "Presentación sin título"} boton="Crear y abrir"
					onClose={() => setDlg(null)} onOk={async (v) => { const [s, p] = destino(); const r = await http.nuevo(campusId, p ? null : s, p, dlg.tipo, v); setDlg(null); setEditor(r.data.id); }} />
			) : null}
			{dlg?.t === "renombrar" ? <DlgNombre titulo="Renombrar" inicial={dlg.n.name} boton="Guardar" onClose={() => setDlg(null)} onOk={async (v) => { await http.actualizar(campusId, dlg.n.id, { name: v }); setDlg(null); cargar(); }} /> : null}
			{dlg?.t === "compartir" ? <DlgCompartir campusId={campusId} nodo={dlg.n} onClose={() => { setDlg(null); cargar(); }} /> : null}
			{dlg?.t === "mover" && ini ? <DlgMover campusId={campusId} espacios={ini.espacios} nodos={dlg.nodos} onClose={() => setDlg(null)} onOk={() => { setDlg(null); setAviso("Movido."); cargar(); }} /> : null}
			{dlg?.t === "versiones" ? <DlgVersiones campusId={campusId} nodo={dlg.n} onClose={() => setDlg(null)} onOk={() => { setDlg(null); setAviso("Versión restaurada."); cargar(); }} /> : null}
			{dlg?.t === "espacio" ? <DlgEspacio campusId={campusId} id={dlg.id} onClose={() => setDlg(null)} onOk={() => { setDlg(null); cargarInicio(); setAviso("Guardado."); }} /> : null}
			{vistaPrevia ? <DlgVista nodo={vistaPrevia.n} url={vistaPrevia.url} onClose={() => setVistaPrevia(null)} onDescargar={puedeBajar ? () => descargar(vistaPrevia.n) : undefined}
				onAnterior={fotoVecina(-1)} onSiguiente={fotoVecina(1)} posicion={posicionFoto()} /> : null}
		</div>
	);
};

export default WappdriveController;
